import React, {useEffect, useLayoutEffect, useState} from 'react'
import {Home} from './home/Home'
import {Layout} from './layout/Layout'
import {Projects} from './projects/Projects'
import {ConfigProvider, App as AntdApp} from 'antd'
import './app.less'
import {
  ActionFunction,
  createBrowserRouter,
  LoaderFunction,
  redirect,
  RouterProvider,
  useNavigate,
} from 'react-router-dom'
import {ErrorPage} from '../error-page'
import {instance as projectsService} from '../services/ProjectsService'
import {instance as toDoService} from '../services/ToDoService'
import {instance as journalService} from '../services/JournalService'
import {instance as plantsService, plantsSortingFields} from '../services/PlantsService'
import {instance as newsService} from '../services/NewsService'
import {instance as gardenPlanVariantsService} from '../services/GardenPlanVariantsService'
import {Project} from './project/Project'
import {NewProject} from './newProject/NewProject'
import {ViewProject} from './viewProject/ViewProject'
import {EditProject} from './editProject/EditProject'
import {instance as userService} from '../services/UserService'
import {LoginPage} from './loginPage/LoginPage'
import {PlantsPage} from './plantsPage/PlantsPage'
import {CanvasEditorPlot} from './canvas/CanvasEditorPlot'
import {CanvasEditorSectors} from './canvas/CanvasEditorSectors'
import {CanvasEditorPlants} from './canvas/CanvasEditorPlants'
import {EditorGeneralProjectInfo} from './canvas/editors/EditorGeneralProjectInfo'
import {IntlProvider} from 'react-intl'
import * as en from '../translations/en.json'
import * as ru from '../translations/ru.json'
import {Locales, LocalizationContext, SupportedLocale} from './LocalizationContext'
import {CanvasEditorBuildings} from './canvas/CanvasEditorBuildings'
import {JournalPage} from './journalPage/JournalPage'
import {LandingPage} from './landingPage/LandingPage'
import {ComingSoonPage} from './comingSoonPage/ComingSoonPage'
import {SignUpPage} from './loginPage/SignUpPage'
import {Policies} from './landingPage/Policies'
import {MousePositionProvider} from './plantForm/usePlantModalMousePosition'
import {Plant, PlantCollection} from '../entities/plant'
import {UserPage} from './userPage/UserPage'
import {NewsPage} from './home/NewsPage'
import {HomeAll} from './home/HomeAll'
import {projectRevalidator} from '../routes/projectRevalidator'
import {
  CustomJournalEventType,
  CustomToDoType,
  JournalEventType,
  JournalRecord,
  ToDoType,
} from '../entities/journalRecord'
import {ToDo} from '../entities/todoList'
import {ToDoPage} from './todos/ToDoPage'
import dayjs from 'dayjs'
import data from '@emoji-mart/data'
import {init} from 'emoji-mart'
import {CanvasEditorHardscape} from './canvas/CanvasEditorHardscape'
import {CanvasEditorWaterscape} from './canvas/CanvasEditorWaterscape'
import {CanvasEditorOtherElements} from './canvas/CanvasEditorOtherElements'
import {plantRevalidator} from '../routes/plantRevalidator'
import {CanvasEditorSolitarius} from './canvas/CanvasEditorSolitarius'
import {todoRevalidator} from '../routes/todoRevalidator'
import {ChangePasswordPage} from './loginPage/ChangePasswordPage'
import {ResetPasswordPage} from './loginPage/ResetPasswordPage'
import {defaultLocale} from '../services/language'
import {NewsListPage} from './home/NewsListPage'

let currentLocale = defaultLocale

export function LoginRedirectToHomeWrapper(props: React.PropsWithChildren) {
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()

  useEffect(() => {
    ;(async () => {
      const user = await userService.getCurrentUser()

      if (user) {
        navigate('/home')
      } else {
        setLoading(false)
      }
    })()
  }, [])

  return loading ? null : (props.children as any)
}

const localizedMessages = {en, ru}

init({data})

export type PlantRouteLoaderData = {
  type: 'newPlant' | 'existingPlant' | 'notFound'
  plant?: Plant
  plantTitlePhoto?: string
  userPlantCollections?: PlantCollection[]
  plantTimeEvents?: JournalRecord[]
  plantTasks?: ToDo[]
  userToDoTypes?: CustomToDoType[]
  userEventTypes?: CustomJournalEventType[]
}

const plantLoader: LoaderFunction = async ({params, request}): Promise<PlantRouteLoaderData> => {
  if (params.plantId === 'new') {
    return {
      type: 'newPlant',
    }
  }
  const plantId = parseInt(params.plantId, 10)
  const getPlantByIdResult = await plantsService.getPlantById(plantId, currentLocale)

  if (!getPlantByIdResult.success) {
    return {
      type: 'notFound',
    }
  }

  const {plant} = getPlantByIdResult

  const [
    plantTitlePhoto,
    userPlantCollections,
    plantTimeEvents,
    plantTasks,
    userToDoTypes,
    userEventTypes,
  ] = await Promise.all([
    plantsService.getTitlePlantPhoto(plant.titlePhotoName, plant.photos[0]),
    plantsService.getUserPlantCollections(),
    journalService.getJournalEventsByPlantId(plantId),
    toDoService.getUserUnfinishedPlantsToDosByPlantId(plantId),
    toDoService.getUserToDoTypes(),
    journalService.getUserJournalEventTypes(),
  ])

  return {
    type: 'existingPlant',
    plant,
    plantTitlePhoto,
    userPlantCollections,
    plantTimeEvents,
    plantTasks,
    userToDoTypes: userToDoTypes.success === true ? userToDoTypes.data : [],
    userEventTypes: userEventTypes.success === true ? userEventTypes.data : [],
  }
}

const journalRecordLoader = async ({params}): Promise<JournalRecord> => {
  const recordId = parseInt(params.recordId, 10)
  const record = await journalService.getUserJournalRecordById(recordId)

  if (record.error === 'notFound') {
    return null
    // TODO: handle non-404 errors to show error message
  }

  return record.success === true ? record.data : null
}

const projectAction: ActionFunction = () => null

const router = createBrowserRouter(
  [
    {
      path: '/',
      element: (
        <LoginRedirectToHomeWrapper>
          <ComingSoonPage />
        </LoginRedirectToHomeWrapper>
      ),
      loader: async () => {
        const user = await userService.getCurrentUser()

        if (user) {
          return redirect('/home')
        }

        return null
      },
    },
    {
      path: '/landing',
      element: (
        <LoginRedirectToHomeWrapper>
          <LandingPage />
        </LoginRedirectToHomeWrapper>
      ),
      loader: async () => {
        const user = await userService.getCurrentUser()

        if (user) {
          return redirect('/home')
        }

        return null
      },
    },
    {
      path: '/login',
      element: <LoginPage />,
    },
    {
      path: '/signup',
      element: <SignUpPage />,
    },
    {
      path: '/change-password',
      element: <ChangePasswordPage />,
      loader: async () => {
        const user = await userService.getCurrentUser()
        if (!user) {
          return redirect('/login')
        }
      },
    },
    {
      path: '/reset-password',
      element: <ResetPasswordPage />,
    },
    {
      path: '/policies',
      element: <Policies />,
    },

    {
      path: '/',
      element: <Layout />,
      errorElement: <ErrorPage showLogout />,
      id: 'root',
      loader: async () => {
        const user = await userService.getCurrentUser()

        if (!user) {
          return redirect('/login')
        }
        const projectNames = await projectsService.getUserProjectsNames()
        return {user, projectNames}
      },
      shouldRevalidate: () => true,
      children: [
        {
          path: '/home',
          id: 'homeAll',
          element: <HomeAll />,
          errorElement: <ErrorPage />,
          loader: async () => {
            const [unfinishedUserToDos, plantedPlantsNamesWithId] = await Promise.all([
              toDoService.getUserUnfinishedToDos(),
              plantsService.getUserPlantsNamesWithId(),
            ])
            return {unfinishedUserToDos, plantedPlantsNamesWithId}
          },

          children: [
            {
              path: '/home',
              id: 'home',
              element: <Home />,
              errorElement: <ErrorPage />,
              loader: async () => {
                const [newsList, projectStatistics, plantedPlantsNamesWithId] = await Promise.all([
                  newsService.getNewsLists(10),
                  projectsService.getUserProjectMaxPlantsInfoStatistics(),
                  plantsService.getUserPlantsNamesWithId(),
                ])
                return {newsList, projectStatistics, plantedPlantsNamesWithId}
              },
            },
            {
              path: '/home/news',
              element: <NewsListPage />,
              errorElement: <ErrorPage />,
              id: 'newsList',
              loader: async ({request}) => {
                const queryParams = new URL(request.url).searchParams
                let page = Number.parseInt(queryParams.get('page'), 10) || 1
                let perPage = newsService.DEFAULT_PER_PAGE

                if (page < 1) {
                  page = 1
                }

                const search = queryParams.get('search')
                const tags = queryParams.get('tags')?.split(',') || []
                const filteredNewsList = await newsService.getFilteredNewsLists(
                  {page, perPage, searchQuery: search, tagFilters: tags},
                  currentLocale
                )

                const maxPossiblePage = Math.ceil(filteredNewsList.totalCount / perPage)

                if (page > maxPossiblePage) {
                  page = maxPossiblePage - 1
                }

                return {
                  newsList: filteredNewsList.newsList,
                  pageParams: {
                    currentPage: page,
                    pageSize: perPage,
                    total: filteredNewsList.totalCount,
                    searchQuery: search,
                    tags: tags,
                  },
                }
              },
            },
            {
              path: '/home/news/:newsURL',
              element: <NewsPage />,
              errorElement: <ErrorPage />,
              id: 'news',
              loader: async ({params}) => {
                const url = params.newsURL
                const [news, newsList] = await Promise.all([
                  newsService.getNewsById(url),
                  newsService.getNewsLists(10),
                ])
                return {news, newsList}
              },
            },
          ],
        },

        {
          path: '/user',
          id: 'user',
          element: <UserPage />,
          errorElement: <ErrorPage />,
          loader: async () => {
            const userSettings = await userService.getUserSettings()
            if (userSettings.success) {
              return {userSettings: userSettings.data}
            } else {
              return null
            }
          },
        },
        {
          path: '/projects',
          element: <Projects />,
          errorElement: <ErrorPage />,
          loader: () => projectsService.getUserProjects(),
        },
        {
          path: '/projects/new',
          element: <NewProject />,
          errorElement: <ErrorPage />,
          action: async ({request}) => {
            const data = await request.formData()
            return redirect(`/projects/${data.get('id')}/edit/plot`)
          },
        },
        {
          path: '/projects/:projectId',
          element: <Project />,
          errorElement: <ErrorPage />,
          id: 'project',
          loader: async ({params}) => {
            const id = params.projectId
            const [
              project,
              projectPlants,
              projectToDos,
              plantedPlantsNamesWithId,
              projectGardenPlanVariants,
            ] = await Promise.all([
              projectsService.getUserProjectById(id),
              plantsService.getProjectPlants(id),
              toDoService.getUserUnfinishedToDos(),
              plantsService.getUserPlantsNamesWithId(),
              gardenPlanVariantsService.getProjectGardenPlanVariants(id),
            ])
            return {
              project,
              projectPlants,
              projectToDos,
              plantedPlantsNamesWithId,
              projectGardenPlanVariants: projectGardenPlanVariants.success
                ? projectGardenPlanVariants.projectGardenPlanVariants
                : [],
            }
          },

          shouldRevalidate: projectRevalidator,
          action: projectAction,
          children: [
            {
              path: '/projects/:projectId/',
              element: <ViewProject />,
              errorElement: <ErrorPage />,
              action: projectAction,
            },
            {
              path: '/projects/:projectId/plants/:plantId',
              id: 'projectWithPlant',
              element: <ViewProject />,
              errorElement: <ErrorPage />,
              loader: plantLoader,
              shouldRevalidate: plantRevalidator,
              action: async ({params, request}) => {
                const formData = await request.formData()
                const {search} = new URL(request.url)
                if (formData.has('id')) {
                  return redirect(
                    `/projects/${params.projectId}/plants/${formData.get('id')}${search}`
                  )
                }

                if (request?.method?.toLowerCase() === 'delete') {
                  return redirect(`/projects/${params.projectId}${search}`)
                }

                return null
              },
            },
            {
              path: '/projects/:projectId/edit',
              element: <EditProject type="edit" />,
              errorElement: <ErrorPage />,
              children: [
                {
                  path: '/projects/:projectId/edit',
                  element: <EditorGeneralProjectInfo />,
                  errorElement: <ErrorPage />,
                  shouldRevalidate: projectRevalidator,
                  action: async ({request}) => {
                    const data = await request.formData()
                    return redirect(
                      `/projects/${data.get('id')}/edit/plot${new URL(request.url).search}`
                    )
                  },
                },
                {
                  path: '/projects/:projectId/edit/plot',
                  element: <CanvasEditorPlot />,
                  errorElement: <ErrorPage />,
                  shouldRevalidate: projectRevalidator,
                  action: projectAction,
                },
                {
                  path: '/projects/:projectId/edit/buildings',
                  element: <CanvasEditorBuildings />,
                  errorElement: <ErrorPage />,
                  shouldRevalidate: projectRevalidator,
                  action: projectAction,
                },
                {
                  path: '/projects/:projectId/edit/hardscape',
                  element: <CanvasEditorHardscape />,
                  errorElement: <ErrorPage />,
                  shouldRevalidate: projectRevalidator,
                  action: projectAction,
                },
                {
                  path: '/projects/:projectId/edit/sectors',
                  element: <CanvasEditorSectors />,
                  errorElement: <ErrorPage />,
                  shouldRevalidate: projectRevalidator,
                  action: projectAction,
                },
                {
                  path: '/projects/:projectId/edit/waterscape',
                  element: <CanvasEditorWaterscape />,
                  errorElement: <ErrorPage />,
                  shouldRevalidate: projectRevalidator,
                  action: projectAction,
                },
                {
                  path: '/projects/:projectId/edit/solitarius',
                  element: <CanvasEditorSolitarius />,
                  errorElement: <ErrorPage />,
                  shouldRevalidate: projectRevalidator,
                  action: projectAction,
                },
                {
                  path: '/projects/:projectId/edit/other',
                  element: <CanvasEditorOtherElements />,
                  errorElement: <ErrorPage />,
                  shouldRevalidate: projectRevalidator,
                  action: projectAction,
                },
                // {
                //   path: '/projects/:projectId/edit/plants',
                //   element: <CanvasEditorPlants />,
                //   errorElement: <ErrorPage />,
                //   shouldRevalidate: projectRevalidator,
                //   action: projectAction,
                // },
              ],
            },
          ],
        },
        {
          path: '/todos',
          id: 'todos',
          element: <ToDoPage />,
          errorElement: <ErrorPage />,
          loader: async () => {
            const [
              plantedPlantsNamesWithId,
              unfinishedToDos,
              userToDoTypes,
              userJournalEventTypes,
              userPlantCollections,
              userPlantCollectionsWithPlantsIds,
            ] = await Promise.all([
              plantsService.getUserPlantsNamesWithId(),
              toDoService.getUserUnfinishedToDos(),
              toDoService.getUserToDoTypes(),
              journalService.getUserJournalEventTypes(),
              plantsService.getUserPlantCollections(),
              plantsService.getUserPlantCollectionsWithPlantsIds(),
            ])

            return {
              plantedPlantsNamesWithId,
              unfinishedToDos,
              userToDoTypes: userToDoTypes.success === true ? userToDoTypes.data : [],
              userJournalEventTypes:
                userJournalEventTypes.success === true ? userJournalEventTypes.data : [],
              userPlantCollections,
              userPlantCollectionsWithPlantsIds,
            }
          },
          shouldRevalidate: todoRevalidator,
          action: async ({request}) => null,
          children: [
            {
              path: '/todos/:todoId/',
              element: <ToDoPage />,
              errorElement: <ErrorPage />,
              shouldRevalidate: todoRevalidator,
            },
            {
              path: '/todos/:todoId/plants/:plantId',
              id: 'toDoWithPlant',
              element: <ToDoPage />,
              errorElement: <ErrorPage />,
              loader: plantLoader,
              shouldRevalidate: ({currentParams, nextParams, formMethod}) => {
                if (formMethod === 'post') {
                  return true
                }

                return currentParams.plantId != nextParams.plantId
              },
              action: async ({params, request}) => {
                const formData = await request.formData()
                if (formData.has('id')) {
                  return redirect(`/todos/${params.todoId}/plants/${formData.get('id')}`)
                }

                return null
              },
            },
          ],
        },
        {
          path: '/plants',
          id: 'plantsList',
          // loader: async ({params}) => {
          //   return {plantsRevalidationToken: Number(new Date())}
          // },
          // shouldRevalidate: ({formMethod}) => formMethod === 'post' || formMethod === 'delete',
          loader: async ({request}) => {
            const queryParams = new URL(request.url).searchParams
            let page = Number.parseInt(queryParams.get('page'), 10) || 1
            let perPage =
              Number.parseInt(queryParams.get('perPage'), 10) || plantsService.DEFAULT_PER_PAGE

            if (page < 1) {
              page = 1
            }

            if (perPage < 5) {
              perPage = 5
            }

            if (perPage > 100) {
              perPage = 100
            }

            let filters = {}

            try {
              filters = JSON.parse(queryParams.get('filter')) || {}
            } catch (ex) {
              console.error('incorrect filter will be ignored')
            }

            const sortOrder = queryParams.get('sortOrder') === 'desc' ? 'desc' : 'asc'
            let sortField = queryParams.get('sortField')
            if (!plantsSortingFields.some((field) => field === sortField)) {
              sortField = 'name'
            }

            // const [plantedPlantsNamesWithId, userJournalEventTypes, userJournalEvents] =
            //   await Promise.all([
            //     plantsService.getUserPlantsNamesWithId(),
            //     journalService.getUserJournalEventTypes(),
            //     journalService.getUserJournalRecords({
            //       page,
            //       perPage,
            //       filters,
            //       sort: sortField
            //         ? {
            //             field: sortField,
            //             direction: sortOrder === 'asc' ? 'asc' : 'desc',
            //           }
            //         : null,
            //     }),
            //   ])

            const userFilteredPlants = await plantsService.getUserFilteredPlants(
              {
                page,
                perPage,
                filters,
                sort: sortField
                  ? {
                      field: sortField,
                      direction: sortOrder,
                    }
                  : null,
              },
              currentLocale
            )

            const maxPossiblePage = Math.ceil(userFilteredPlants.totalCount / perPage)

            if (page > maxPossiblePage) {
              page = maxPossiblePage - 1
            }

            const userPlantCollections = await plantsService.getUserPlantCollections()

            return {
              userPlants: userFilteredPlants.plants,
              userPlantCollections: userPlantCollections,
              totalCount: userFilteredPlants.totalCount,
              tableParams: {page, perPage, sortField, sortOrder, filters},
              dataKey: Date.now(),
            }
          },
          shouldRevalidate: ({formMethod, currentUrl, nextUrl}) => {
            if (formMethod === 'post' || formMethod === 'delete') {
              return true
            }

            const currentQueryParams = new URL(currentUrl).searchParams
            const nextQueryParams = new URL(nextUrl).searchParams

            // `view` (Cards/Gallery/Table) is a display-only param and does not
            // affect the fetched data, so changing it alone must not refetch.
            currentQueryParams.delete('view')
            nextQueryParams.delete('view')

            if (currentQueryParams.size !== nextQueryParams.size) {
              return true
            }

            for (const [param, value] of currentQueryParams.entries()) {
              if (value !== nextQueryParams.get(param)) {
                return true
              }
            }

            return false
          },
          children: [
            {
              path: '',
              element: <PlantsPage />,
              errorElement: <ErrorPage />,
            },
            {
              path: '/plants/:plantId',
              id: 'plant',
              element: <PlantsPage />,
              errorElement: <ErrorPage />,
              loader: plantLoader,
              shouldRevalidate: plantRevalidator,
              action: async ({request}) => {
                if (request.method === 'DELETE') {
                  return redirect(`/plants`)
                }

                const formData = await request.formData()

                if (formData.has('id')) {
                  return redirect(`/plants/${formData.get('id')}`)
                }

                return null
              },
            },
          ],
        },
        {
          path: '/journal',
          id: 'journal',
          element: <JournalPage />,
          errorElement: <ErrorPage />,
          shouldRevalidate: ({formMethod, currentUrl, nextUrl}) => {
            if (formMethod === 'post' || formMethod === 'delete') {
              return true
            }

            const currentQueryParams = new URL(currentUrl).searchParams
            const nextQueryParams = new URL(nextUrl).searchParams

            if (currentQueryParams.size !== nextQueryParams.size) {
              return true
            }

            for (const [param, value] of currentQueryParams.entries()) {
              if (value !== nextQueryParams.get(param)) {
                return true
              }
            }

            return false
          },
          action: async ({request}) => null,
          loader: async ({request}) => {
            const queryParams = new URL(request.url).searchParams
            let page = Number.parseInt(queryParams.get('page'), 10) || 1
            let perPage = Number.parseInt(queryParams.get('perPage'), 10) || 10

            if (page < 1) {
              page = 1
            }

            if (perPage < 5) {
              perPage = 5
            }

            if (perPage > 100) {
              perPage = 100
            }

            let filters = {}

            try {
              filters = JSON.parse(queryParams.get('filter')) || {}
            } catch (ex) {
              console.error('incorrect filter will be ignored')
            }

            const sortOrder = queryParams.get('sortOrder')
            const sortField = queryParams.get('sortField')

            const [plantedPlantsNamesWithId, userJournalEventTypes, userJournalEvents] =
              await Promise.all([
                plantsService.getUserPlantsNamesWithId(),
                journalService.getUserJournalEventTypes(),
                journalService.getUserJournalRecords({
                  page,
                  perPage,
                  filters,
                  sort: sortField
                    ? {
                        field: sortField,
                        direction: sortOrder === 'asc' ? 'asc' : 'desc',
                      }
                    : null,
                }),
              ])

            const maxPossiblePage = Math.ceil(userJournalEvents.count / perPage)

            if (page > maxPossiblePage) {
              page = maxPossiblePage - 1
            }

            return {
              plantedPlantsNamesWithId,
              userJournalEventTypes:
                userJournalEventTypes.success === true ? userJournalEventTypes.data : [],
              userJournalEvents: userJournalEvents,
              tableParams: {page, perPage, sortField, sortOrder},
            }
          },
          children: [
            {
              path: '/journal/:recordId/',
              id: 'journalRecordById',
              element: <JournalPage />,
              errorElement: <ErrorPage />,
              loader: journalRecordLoader,
              shouldRevalidate: ({formMethod}) => formMethod === 'post' || formMethod === 'delete',
              action: ({request}) => {
                if (request.method === 'DELETE') {
                  return redirect('/journal')
                }
              },
            },
            {
              path: '/journal/:recordId/plants/:plantId',
              id: 'journalRecordWithPlant',
              element: <JournalPage />,
              errorElement: <ErrorPage />,
              loader: plantLoader,
              shouldRevalidate: ({currentParams, nextParams, formMethod}) => {
                if (formMethod === 'post') {
                  return true
                }

                return currentParams.plantId != nextParams.plantId
              },
              action: async ({params, request}) => {
                if (request.method === 'DELETE') {
                  return redirect(`/journal`)
                }

                const formData = await request.formData()
                if (formData.has('id')) {
                  return redirect(`/journal/${params.recordId}/plants/${formData.get('id')}`)
                }

                return null
              },
            },
          ],
        },
        // {
        //   path: '/snapshots',
        //   element: <Projects isSnapshot={true} />,
        //   errorElement: <ErrorPage />,
        //   loader: () => projectsService.getUserSnapshots(),
        // },
        // {
        //   path: '/snapshots/:projectId',
        //   element: <ViewProject isSnapshot={true} />,
        //   errorElement: <ErrorPage />,
        //   id: 'snapshot',
        //   loader: async ({params}) => {
        //     const id = parseInt(params.projectId, 10)
        //     const [project, projectPlants] = await Promise.all([
        //       projectsService.getUserProjectById(id),
        //       plantsService.getProjectPlants(id),
        //     ])
        //     return {project, projectPlants}
        //   },
        // }, // snapshots are disabled
      ],
    },
  ],
  {
    basename:
      process.env.BASE_PATH ||
      (process.env.NODE_ENV === 'development' ? '/' : '/terrasketch-frontend'),
  }
)

export function App() {
  const [locale, setLocaleState] = useState<SupportedLocale>(defaultLocale)
  const localeInfo = Locales[locale]

  const setLocale = (newLocale) => {
    const newLocaleInfo = Locales[newLocale]
    dayjs.locale(newLocaleInfo.dayjs)
    setLocaleState(newLocale)
  }

  useEffect(() => {
    currentLocale = locale
  }, [locale])

  return (
    <AntdApp>
      <LocalizationContext.Provider value={{setLocale, getLocale: () => locale}}>
        <IntlProvider
          messages={localizedMessages[locale]}
          locale={locale}
          defaultLocale={defaultLocale}
          onError={() => {}}
        >
          <ConfigProvider
            theme={{
              token: {
                colorPrimary: '#F48254', // orange[6],
                colorLink: '#F2703D', // orange[5],
                colorLinkHover: '#F8A483', // orange[8],
                colorBgContainer: '#FAF8F5',
                fontSize: 16,
                fontFamily: "'Open Sans', sans-serif",
              },
              components: {
                Layout: {
                  headerBg: '#F2703D', // orange[5],
                  colorLink: 'white',
                },
                Menu: {},
              },
            }}
            locale={localeInfo.antd}
            warning={{
              strict: false,
            }}
          >
            <MousePositionProvider>
              <RouterProvider router={router} />
            </MousePositionProvider>
          </ConfigProvider>
        </IntlProvider>
      </LocalizationContext.Provider>
    </AntdApp>
  )
}
